<script setup lang="ts">
import type { PhoneInputType } from "./types";
import { useI18n } from "vue-i18n";
import { ref, computed, watch } from "vue";
import { VueTelInput } from "vue-tel-input";

export interface Props {
  value?: PhoneInputType;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  customValidate?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  value: () => ({
    userInput: "",
    formattedNumber: "",
    error: null,
  }),
  placeholder: "",
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  (event: "input", input: PhoneInputType): void;
  (event: "blur"): void;
}>();

const { t, locale } = useI18n();

const phone = ref(props.value.userInput);
const error = ref<string | null>(null);

const phoneInputSettings = computed(() => ({
  validCharactersOnly: true,
  preferredCountries: ["US", "GB", "AU", "CA", "NZ"],
  autoDefaultCountry: true,
  inputOptions: {
    autofocus: false,
    required: props.required,
    placeholder: props.placeholder,
  },
  dropdownOptions: {
    showFlags: true,
    showDialCodeInSelection: false,
    showDialCodeInList: true,
  },
}));

watch(
  () => props.value,
  (val) => {
    if (phone.value?.replace(/\s/g, "") === val.userInput) return;
    phone.value = val.userInput;
  },
  { deep: true }
);

const isValidFormat = ref(false);

const onInput = (
  userInput: string,
  phoneObject: { formatted: string; valid: boolean }
) => {
  const { formatted: formattedNumber, valid: isValid } = phoneObject;
  if (props.customValidate) {
    emit("input", {
      formattedNumber,
      userInput,
      isValid,
      error: null, //because custom validate so the error will be default null
    });
    return;
  }
  isValidFormat.value = !!isValid;
  let err = null;
  if (userInput && !isValid) {
    err = t("validation.invalidPhone");
  }

  error.value = err;

  emit("input", {
    formattedNumber,
    userInput,
    error: error.value,
  });
};

watch(locale, () => {
  if (phone.value) {
    const phoneObject = {
      formatted: props.value.formattedNumber,
      valid: isValidFormat.value,
    };
    onInput(phone.value, phoneObject);
  }
});
</script>

<template>
  <vue-tel-input
    v-model="phone"
    v-bind="phoneInputSettings"
    mode="international"
    :disabled="disabled"
    :class="{ error: error }"
    @on-input="onInput"
    @blur="emit('blur')"
  >
    <template #arrow-icon="{ open }">
      <font-awesome-icon
        :icon="['fal', open ? 'chevron-up' : 'chevron-down']"
      />
    </template>
  </vue-tel-input>
</template>

<style scoped lang="scss">
.vue-tel-input {
  border: 1px solid var(--so-border-color);
  background: var(--so-fill-color-blank);
  height: 40px;

  &:hover {
    border: 1px solid var(--so-text-color-secondary);
  }

  &:focus-within {
    box-shadow: none;
    border: 1px solid var(--so-color-primary);
  }

  &.error {
    border-color: var(--so-color-error);
  }

  &.disabled {
    background: var(--so-fill-color-lighter);
    border: 1px solid var(--so-border-color-light);

    :deep(.vti__flag) {
      opacity: 0.5;
    }

    .vti__selection svg {
      color: var(--so-text-color-disabled);
    }

    :deep(.vti__input),
    :deep(.vti__dropdown:hover) {
      background-color: var(--so-fill-color-lighter);
      color: var(--so-text-color-placeholder);
    }
  }

  :deep(.vti__input) {
    line-height: normal;
    background-color: var(--so-fill-color-blank);
    color: var(--so-text-color-primary);
  }

  :deep(.vti__input::placeholder) {
    color: var(--so-text-color-placeholder);
    font-size: 0.875rem;
  }

  :deep(.vti__flag) {
    padding-right: 20px;
  }

  :deep(.vti__dropdown-list) {
    background-color: var(--so-fill-color-blank);

    &.below {
      top: 39px;
    }
  }

  :deep(.vti__dropdown) {
    &:hover,
    &.open,
    .vti__dropdown-item.highlighted {
      background-color: var(--so-fill-color-light);
    }
  }
}
</style>
